import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Search,
  UserCheck,
  UserX,
  Shield,
  Eye,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { merchantService, type MerchantUser as ApiMerchantUser } from '../services/merchantService'
import { authService } from '../services/authService'
import { userService } from '../services/userService'

interface MerchantUser {
  id: string
  merchantId: string
  merchantName: string
  userId: string
  userEmail: string
  userName: string
  userFirstName: string
  userLastName: string
  userPhone: string
  permissionLevel: 'owner' | 'admin' | 'manager' | 'viewer'
  canManageUsers: boolean
  canManageProducts?: boolean
  canManageOffers?: boolean
  canViewReports?: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string | null
}

interface AddUserFormData {
  email: string
  firstName: string
  lastName: string
  phone: string
  password: string
  confirmPassword: string
  permissionLevel: 'owner' | 'admin' | 'manager' | 'viewer'
  canManageUsers: boolean
  canManageProducts: boolean
  canManageOffers: boolean
  canViewReports: boolean
  isActive: boolean
  selectedMerchantIds: string[] // Pour la sélection multiple de commerces
}

interface EditUserFormData {
  permissionLevel: 'owner' | 'admin' | 'manager' | 'viewer'
  canManageUsers: boolean
  canManageProducts: boolean
  canManageOffers: boolean
  canViewReports: boolean
  isActive: boolean
}

const MesUtilisateursPage = () => {
const [searchParams] = useSearchParams()
const currentUser = authService.getCurrentUser()
const [merchantUsers, setMerchantUsers] = useState<MerchantUser[]>([])
const [loading, setLoading] = useState(true)
const [searchTerm, setSearchTerm] = useState('')
const [selectedPermission, setSelectedPermission] = useState<string>('all')
const [selectedStatus, setSelectedStatus] = useState<string>('all')
const [showAddModal, setShowAddModal] = useState(false)
const [showEditModal, setShowEditModal] = useState(false)
const [selectedUser, setSelectedUser] = useState<MerchantUser | null>(null)
const [addFormData, setAddFormData] = useState<AddUserFormData>({
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  password: '',
  confirmPassword: '',
  permissionLevel: 'manager',
  canManageUsers: false,
  canManageProducts: false,
  canManageOffers: false,
  canViewReports: false,
  isActive: true,
  selectedMerchantIds: [] // Initialiser avec un tableau vide
})
const [editFormData, setEditFormData] = useState<EditUserFormData>({
  permissionLevel: 'manager',
  canManageUsers: false,
  canManageProducts: false,
  canManageOffers: false,
  canViewReports: false,
  isActive: true
})
const [submitting, setSubmitting] = useState(false)
const [currentMerchantId, setCurrentMerchantId] = useState<string>('')
const [merchants, setMerchants] = useState<any[]>([])
// Initialize selectedMerchantId from URL query parameter if present and user is admin
const initialMerchantId = searchParams.get('merchantId') || 'all'
const [selectedMerchantId, setSelectedMerchantId] = useState<string>(initialMerchantId)
const [isAdmin, setIsAdmin] = useState(false)
const [canManageUsers, setCanManageUsers] = useState(false) // Si l'utilisateur peut gérer les utilisateurs
const [childMerchants, setChildMerchants] = useState<any[]>([]) // Pour stocker les sous-commerces
const [loadingChildren, setLoadingChildren] = useState(false)

  useEffect(() => {
    // Check if user is admin and load data
    const adminCheck = authService.hasRole('admin')
    console.log('Initial admin check:', adminCheck)
    setIsAdmin(adminCheck)
    
    // Check if user can manage users (admin OR merchant with manageUsers permission)
    const canManage = adminCheck || authService.hasMerchantPermission('manageUsers')
    console.log('Can manage users:', canManage)
    setCanManageUsers(canManage)
    
    // Load data with the correct admin status
    loadData(adminCheck)
  }, [])

  useEffect(() => {
    // Reload data when merchant selection changes (for admin users)
    if (isAdmin && selectedMerchantId) {
      console.log('Merchant selection changed, reloading data...')
      loadData(isAdmin)
    }
  }, [selectedMerchantId])

  const loadData = async (adminStatus?: boolean) => {
    try {
      const actualAdminStatus = adminStatus !== undefined ? adminStatus : isAdmin
      setLoading(true)
      console.log('loadData called, isAdmin:', isAdmin, 'actualAdminStatus:', actualAdminStatus, 'selectedMerchantId:', selectedMerchantId)
      
      let merchantUsersList: MerchantUser[] = []
      
      if (actualAdminStatus) {
        console.log('Admin user detected, loading all merchants...')
        // Admin user: load all merchants and all merchant users
        // Load all merchants first
        const merchantsResponse = await merchantService.getAll({ page: 1, limit: 1000 })
        console.log('Merchants response:', merchantsResponse)
        const allMerchants = merchantsResponse.merchants || []
        console.log('All merchants:', allMerchants.length, 'merchants found')
        setMerchants(allMerchants)
        
        // If a specific merchant is selected, load only that merchant's users
        if (selectedMerchantId && selectedMerchantId !== 'all') {
          console.log('Loading users for specific merchant:', selectedMerchantId)
          const response = await merchantService.getMerchantUsers(selectedMerchantId)
          console.log('Merchant users response for', selectedMerchantId, ':', response)
          merchantUsersList = response.users.map((mu: ApiMerchantUser) => ({
            id: mu.merchant_user_id,
            merchantId: mu.merchant_id,
            merchantName: mu.merchant_name || allMerchants.find(m => m.merchant_id === mu.merchant_id)?.business_name || 'Commerce inconnu',
            userId: mu.user_id,
            userEmail: mu.user_email || '',
            userName: `${mu.user_first_name || ''} ${mu.user_last_name || ''}`.trim() || mu.user_email || '',
            userFirstName: mu.user_first_name || '',
            userLastName: mu.user_last_name || '',
            userPhone: mu.user_phone || '',
            permissionLevel: mu.permission_level as 'owner' | 'admin' | 'manager' | 'viewer',
            canManageUsers: mu.can_manage_users || false,
            canManageProducts: mu.can_manage_products || false,
            canManageOffers: mu.can_manage_offers || false,
            canViewReports: mu.can_view_reports || false,
            isActive: mu.is_active,
            createdAt: mu.created_at,
            updatedAt: mu.updated_at,
            createdBy: mu.created_by || null
          }))
        } else {
          console.log('Loading users from ALL merchants (selectedMerchantId is "all" or empty)')
          // Load users from all merchants
          const allUsersPromises = allMerchants.map(async (merchant) => {
            try {
              console.log('Loading users for merchant:', merchant.merchant_id, merchant.business_name)
              const response = await merchantService.getMerchantUsers(merchant.merchant_id)
              console.log(`Found ${response.users?.length || 0} users for merchant ${merchant.merchant_id}`)
              return response.users.map((mu: ApiMerchantUser) => ({
                id: mu.merchant_user_id,
                merchantId: mu.merchant_id,
                merchantName: mu.merchant_name || merchant.business_name,
                userId: mu.user_id,
                userEmail: mu.user_email || '',
                userName: `${mu.user_first_name || ''} ${mu.user_last_name || ''}`.trim() || mu.user_email || '',
                userFirstName: mu.user_first_name || '',
                userLastName: mu.user_last_name || '',
                userPhone: mu.user_phone || '',
                permissionLevel: mu.permission_level as 'owner' | 'admin' | 'manager' | 'viewer',
                canManageUsers: mu.can_manage_users || false,
                canManageProducts: mu.can_manage_products || false,
                canManageOffers: mu.can_manage_offers || false,
                canViewReports: mu.can_view_reports || false,
                isActive: mu.is_active,
                createdAt: mu.created_at,
                updatedAt: mu.updated_at,
                createdBy: mu.created_by || null
              }))
            } catch (err) {
              console.error(`Error loading users for merchant ${merchant.merchant_id}:`, err)
              return []
            }
          })
          
          console.log('Waiting for all merchant user promises...')
          const allUsersArrays = await Promise.all(allUsersPromises)
          console.log('All users arrays:', allUsersArrays)
          merchantUsersList = allUsersArrays.flat()
          console.log('Flattened merchant users list:', merchantUsersList.length, 'users found')
        }
        
        // For admin, set current merchant to first merchant or empty
        if (allMerchants.length > 0 && !currentMerchantId) {
          console.log('Setting currentMerchantId to first merchant:', allMerchants[0].merchant_id)
          setCurrentMerchantId(allMerchants[0].merchant_id)
        }
      } else {
        console.log('Non-admin user, loading user merchant...')
        // Non-admin user: get current user's merchant
        const currentUserId = currentUser?.userId || ''
        console.log('Current user ID:', currentUserId)
        if (!currentUserId) {
          console.error('Unable to resolve current user id from auth context')
          toast.error('Impossible d\'identifier l\'utilisateur courant')
          return
        }
        const userMerchant = await merchantService.getByUserId(currentUserId)
        console.log('User merchant response:', userMerchant)
        if (!userMerchant?.merchant_id) {
          toast.error('Vous n\'êtes pas associé à un commerce')
          return
        }
        
        setCurrentMerchantId(userMerchant.merchant_id)
        
        // Load merchant users for current merchant
        const response = await merchantService.getMerchantUsers(userMerchant.merchant_id)
        console.log('Merchant users response for non-admin:', response)
        merchantUsersList = response.users.map((mu: ApiMerchantUser) => ({
          id: mu.merchant_user_id,
          merchantId: mu.merchant_id,
          merchantName: mu.merchant_name || userMerchant.business_name,
          userId: mu.user_id,
          userEmail: mu.user_email || '',
          userName: `${mu.user_first_name || ''} ${mu.user_last_name || ''}`.trim() || mu.user_email || '',
          userFirstName: mu.user_first_name || '',
          userLastName: mu.user_last_name || '',
          userPhone: mu.user_phone || '',
          permissionLevel: mu.permission_level as 'owner' | 'admin' | 'manager' | 'viewer',
          canManageUsers: mu.can_manage_users || false,
          canManageProducts: mu.can_manage_products || false,
          canManageOffers: mu.can_manage_offers || false,
          canViewReports: mu.can_view_reports || false,
          isActive: mu.is_active,
          createdAt: mu.created_at,
          updatedAt: mu.updated_at,
          createdBy: mu.created_by || null
        }))
      }
      
      console.log('Final merchantUsersList to set:', merchantUsersList.length, 'users')
      setMerchantUsers(merchantUsersList)
    } catch (error: any) {
      console.error('Error loading merchant users:', error)
      
      // Extract error message from API response if available
      let errorMessage = 'Erreur lors du chargement des utilisateurs'
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
    } finally {
      console.log('loadData finished, setting loading to false')
      setLoading(false)
    }
  }

  // Fonction pour charger les sous-commerces d'un commerce parent
  const loadChildMerchants = async (parentMerchantId: string) => {
    if (!parentMerchantId) {
      setChildMerchants([])
      return
    }
    
    try {
      setLoadingChildren(true)
      // Utiliser l'endpoint /merchants/:id/children pour récupérer les sous-commerces
      const response = await merchantService.getMerchantChildren(parentMerchantId)
      console.log('Child merchants response:', response)
      setChildMerchants(response.children || [])
    } catch (error) {
      console.error('Error loading child merchants:', error)
      setChildMerchants([])
    } finally {
      setLoadingChildren(false)
    }
  }

  // Effet pour charger les sous-commerces quand un commerce parent est sélectionné
  useEffect(() => {
    // Pour les admins: charger les sous-commerces du commerce sélectionné
    // Pour les marchands avec permission de gérer les utilisateurs: charger les sous-commerces de leur commerce courant
    if (isAdmin && selectedMerchantId && selectedMerchantId !== 'all') {
      loadChildMerchants(selectedMerchantId)
    } else if (canManageUsers && !isAdmin && currentMerchantId) {
      // Pour les marchands avec permission de gérer les utilisateurs, charger les sous-commerces de leur commerce
      loadChildMerchants(currentMerchantId)
    } else {
      setChildMerchants([])
    }
  }, [selectedMerchantId, isAdmin, canManageUsers, currentMerchantId])

  const filteredMerchantUsers = merchantUsers.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userPhone?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesPermission = selectedPermission === 'all' || user.permissionLevel === selectedPermission
    const matchesStatus = selectedStatus === 'all' || 
      (selectedStatus === 'active' && user.isActive) ||
      (selectedStatus === 'inactive' && !user.isActive)
    
    return matchesSearch && matchesPermission && matchesStatus
  })

  const handleAddUser = () => {
    setAddFormData({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      password: '',
      confirmPassword: '',
      permissionLevel: 'manager',
      canManageUsers: false,
      canManageProducts: false,
      canManageOffers: false,
      canViewReports: false,
      isActive: true,
      selectedMerchantIds: []
    })
    setShowAddModal(true)
  }

  const handleEditUser = (user: MerchantUser) => {
    // Prevent editing current user
    if (user.userId === currentUser?.userId) {
      toast.error('Vous ne pouvez pas modifier votre propre compte')
      return
    }
    
    setSelectedUser(user)
    setEditFormData({
      permissionLevel: user.permissionLevel,
      canManageUsers: user.canManageUsers || false,
      canManageProducts: user.canManageProducts || false,
      canManageOffers: user.canManageOffers || false,
      canViewReports: user.canViewReports || false,
      isActive: user.isActive
    })
    setShowEditModal(true)
  }

  const handleDeleteUser = async (user: MerchantUser) => {
    // Prevent deleting current user
    if (user.userId === currentUser?.userId) {
      toast.error('Vous ne pouvez pas supprimer votre propre compte')
      return
    }
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${user.userName} de votre commerce ?`)) {
      return
    }

    try {
      await merchantService.removeMerchantUser(user.merchantId, user.userId)
      toast.success('Utilisateur supprimé avec succès')
      loadData(isAdmin)
    } catch (error) {
      console.error('Error deleting merchant user:', error)
      toast.error('Erreur lors de la suppression de l\'utilisateur')
    }
  }

  const handleToggleStatus = async (user: MerchantUser) => {
    // Prevent toggling status of current user
    if (user.userId === currentUser?.userId) {
      toast.error('Vous ne pouvez pas modifier le statut de votre propre compte')
      return
    }
    
    try {
      await merchantService.updateMerchantUser(user.merchantId, user.userId, {
        is_active: !user.isActive
      })
      toast.success(`Statut ${!user.isActive ? 'activé' : 'désactivé'} avec succès`)
      loadData(isAdmin)
    } catch (error) {
      console.error('Error toggling user status:', error)
      toast.error('Erreur lors du changement de statut')
    }
  }

  const handleAddSubmit = async () => {
    // Validation
    if (!addFormData.email || !addFormData.firstName || !addFormData.lastName || !addFormData.phone || !addFormData.password) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    if (addFormData.password !== addFormData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas')
      return
    }

    // For admin users or merchant users with canManageUsers permission, check if a merchant is selected
    let targetMerchantIds: string[] = []
    
    if (isAdmin) {
      if (selectedMerchantId === 'all' || !selectedMerchantId) {
        toast.error('Veuillez sélectionner un commerce parent pour ajouter un utilisateur')
        return
      }
      
      // Toujours inclure le commerce parent
      targetMerchantIds.push(selectedMerchantId)
      
      // Ajouter les sous-commerces sélectionnés
      if (addFormData.selectedMerchantIds.length > 0) {
        targetMerchantIds = [...targetMerchantIds, ...addFormData.selectedMerchantIds]
      }
    } else if (canManageUsers) {
      // Pour les marchands avec permission de gérer les utilisateurs
      if (!currentMerchantId) {
        toast.error('Aucun commerce sélectionné')
        return
      }
      
      // Toujours inclure le commerce parent (leur commerce courant)
      targetMerchantIds.push(currentMerchantId)
      
      // Ajouter les sous-commerces sélectionnés
      if (addFormData.selectedMerchantIds.length > 0) {
        targetMerchantIds = [...targetMerchantIds, ...addFormData.selectedMerchantIds]
      }
    } else {
      // Pour les autres non-admins (sans permission de gérer les utilisateurs), utiliser uniquement le commerce courant
      if (!currentMerchantId) {
        toast.error('Aucun commerce sélectionné')
        return
      }
      targetMerchantIds.push(currentMerchantId)
    }

    // Supprimer les doublons
    targetMerchantIds = [...new Set(targetMerchantIds)]

    if (targetMerchantIds.length === 0) {
      toast.error('Aucun commerce sélectionné')
      return
    }

    try {
      setSubmitting(true)
      
      // 1. Créer l'utilisateur via user-service (comme sur la page Users)
      // Les utilisateurs créés via MesUtilisateursPage sont des utilisateurs marchands
      const newUser = await userService.create({
        email: addFormData.email,
        first_name: addFormData.firstName,
        last_name: addFormData.lastName,
        phone: addFormData.phone,
        password: addFormData.password,
        role: 'merchant', // Utilisateurs marchands
        is_verified: true // Les utilisateurs créés par un admin sont automatiquement vérifiés
      })

      if (!newUser || !newUser.user_id) {
        throw new Error('Échec de la création de l\'utilisateur')
      }

      // 2. Ajouter l'utilisateur à tous les commerces sélectionnés
      const addPromises = targetMerchantIds.map(merchantId =>
        merchantService.addMerchantUser(merchantId, {
          user_id: newUser.user_id,
          permission_level: addFormData.permissionLevel,
          can_manage_users: addFormData.canManageUsers,
          can_manage_products: addFormData.canManageProducts,
          can_manage_offers: addFormData.canManageOffers,
          can_view_reports: addFormData.canViewReports,
          is_active: addFormData.isActive
        })
      )

      await Promise.all(addPromises)

      toast.success(`Utilisateur créé et ajouté à ${targetMerchantIds.length} commerce(s) avec succès`)
      setShowAddModal(false)
      loadData(isAdmin)
    } catch (error: any) {
      console.error('Error creating and adding user:', error)
      
      // Gestion des erreurs spécifiques
      if (error.response?.data?.error?.includes('already exists') || error.message?.includes('already exists')) {
        toast.error('Un utilisateur avec cet email existe déjà')
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error)
      } else {
        toast.error('Erreur lors de la création de l\'utilisateur')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditSubmit = async () => {
    if (!selectedUser) return
    
    // Prevent editing current user (double-check)
    if (selectedUser.userId === currentUser?.userId) {
      toast.error('Vous ne pouvez pas modifier votre propre compte')
      setShowEditModal(false)
      return
    }

    try {
      setSubmitting(true)
      await merchantService.updateMerchantUser(selectedUser.merchantId, selectedUser.userId, {
        permission_level: editFormData.permissionLevel,
        can_manage_users: editFormData.canManageUsers,
        can_manage_products: editFormData.canManageProducts,
        can_manage_offers: editFormData.canManageOffers,
        can_view_reports: editFormData.canViewReports,
        is_active: editFormData.isActive
      })
      toast.success('Utilisateur modifié avec succès')
      setShowEditModal(false)
      loadData(isAdmin)
    } catch (error) {
      console.error('Error updating merchant user:', error)
      toast.error('Erreur lors de la modification de l\'utilisateur')
    } finally {
      setSubmitting(false)
    }
  }

  const getPermissionColor = (level: string) => {
    switch (level) {
      case 'owner': return 'bg-purple-100 text-purple-800'
      case 'admin': return 'bg-blue-100 text-blue-800'
      case 'manager': return 'bg-green-100 text-green-800'
      case 'viewer': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPermissionIcon = (level: string) => {
    switch (level) {
      case 'owner': return <Shield className="h-4 w-4" />
      case 'admin': return <UserCheck className="h-4 w-4" />
      case 'manager': return <Edit className="h-4 w-4" />
      case 'viewer': return <Eye className="h-4 w-4" />
      default: return <Users className="h-4 w-4" />
    }
  }

  const getPermissionLabel = (level: string) => {
    switch (level) {
      case 'owner': return 'Propriétaire'
      case 'admin': return 'Administrateur'
      case 'manager': return 'Gestionnaire'
      case 'viewer': return 'Observateur'
      default: return level
    }
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Chargement des utilisateurs...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="h-6 w-6 mr-2" />
            {isAdmin ? 'Utilisateurs des Commerces' : 'Mes Utilisateurs'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isAdmin
              ? 'Gérez les utilisateurs de tous les commerces'
              : 'Gérez les utilisateurs de votre commerce'}
          </p>
        </div>
        
        <button
          onClick={handleAddUser}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-5 w-5 mr-2" />
          Ajouter un utilisateur
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, email ou téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Merchant filter (only for admin) */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commerce
              </label>
              <select
                value={selectedMerchantId}
                onChange={(e) => setSelectedMerchantId(e.target.value)}
                className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="all">Tous les commerces</option>
                {merchants.map((merchant) => (
                  <option key={merchant.merchant_id} value={merchant.merchant_id}>
                    {merchant.business_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Permission filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permission
            </label>
            <select
              value={selectedPermission}
              onChange={(e) => setSelectedPermission(e.target.value)}
              className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="all">Toutes les permissions</option>
              <option value="owner">Propriétaire</option>
              <option value="admin">Administrateur</option>
              <option value="manager">Gestionnaire</option>
              <option value="viewer">Observateur</option>
            </select>
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Ajouter un nouvel utilisateur</h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  Renseignez les informations du nouvel utilisateur ainsi que ses droits d'accès.
                </p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Fermer"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5">
          
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="md:col-span-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Rattachement au commerce
                </h4>
              </div>
            {/* Merchant information */}
            {(isAdmin || canManageUsers) ? (
              <>
                {isAdmin ? (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commerce parent *
                    </label>
                    <select
                      value={selectedMerchantId === 'all' ? '' : selectedMerchantId}
                      onChange={(e) => setSelectedMerchantId(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="">Sélectionnez un commerce parent</option>
                      {merchants.map((merchant) => (
                        <option key={merchant.merchant_id} value={merchant.merchant_id}>
                          {merchant.business_name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-sm text-gray-500">
                      Sélectionnez le commerce parent pour voir ses sous-commerces
                    </p>
                  </div>
                ) : (
                  <div className="md:col-span-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Users className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">Commerce parent</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Vous gérez les utilisateurs pour le commerce:
                            <span className="font-medium ml-1">
                              {merchants.find(m => m.merchant_id === currentMerchantId)?.business_name || 'Votre commerce'}
                            </span>
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Vous pouvez attribuer des sous-commerces à cet utilisateur.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sous-commerces (enfants) */}
                {((isAdmin && selectedMerchantId && selectedMerchantId !== 'all') || (!isAdmin && canManageUsers && currentMerchantId)) && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sous-commerces à gérer
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                      {loadingChildren ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-primary-600 mr-2" />
                          <span className="text-sm text-gray-600">Chargement des sous-commerces...</span>
                        </div>
                      ) : childMerchants.length > 0 ? (
                        <div className="space-y-2">
                          {childMerchants.map((child) => (
                            <div key={child.merchant_id} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`child-${child.merchant_id}`}
                                checked={addFormData.selectedMerchantIds.includes(child.merchant_id)}
                                onChange={(e) => {
                                  const isChecked = e.target.checked
                                  setAddFormData(prev => ({
                                    ...prev,
                                    selectedMerchantIds: isChecked
                                      ? [...prev.selectedMerchantIds, child.merchant_id]
                                      : prev.selectedMerchantIds.filter(id => id !== child.merchant_id)
                                  }))
                                }}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <label htmlFor={`child-${child.merchant_id}`} className="ml-2 block text-sm text-gray-700">
                                <span className="font-medium">{child.business_name}</span>
                                <span className="text-xs text-gray-500 ml-2">({child.business_type})</span>
                                {!child.is_active && (
                                  <span className="text-xs text-red-600 ml-2">(Inactif)</span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-600">
                            Aucun sous-commerce trouvé pour ce commerce parent.
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            L'utilisateur sera uniquement rattaché au commerce parent.
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Cochez les sous-commerces que cet utilisateur pourra gérer
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="md:col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">Commerce associé</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Cet utilisateur sera automatiquement rattaché à votre commerce actuel.
                        {currentMerchantId && merchants.length > 0 && (
                          <span className="font-medium ml-1">
                            {merchants.find(m => m.merchant_id === currentMerchantId)?.business_name || 'Votre commerce'}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section : informations personnelles */}
            <div className="md:col-span-2 mt-2 border-t border-gray-200 pt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Informations personnelles
              </h4>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={addFormData.email}
                onChange={(e) => setAddFormData({...addFormData, email: e.target.value})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="exemple@email.com"
                required
                autoComplete="off"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prénom *
              </label>
              <input
                type="text"
                value={addFormData.firstName}
                onChange={(e) => setAddFormData({...addFormData, firstName: e.target.value})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Jean"
                required
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom *
              </label>
              <input
                type="text"
                value={addFormData.lastName}
                onChange={(e) => setAddFormData({...addFormData, lastName: e.target.value})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Dupont"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Téléphone *
              </label>
              <input
                type="tel"
                value={addFormData.phone}
                onChange={(e) => setAddFormData({...addFormData, phone: e.target.value})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="+33 1 23 45 67 89"
                required
                autoComplete="off"
              />
            </div>

            {/* Section : identifiants de connexion */}
            <div className="md:col-span-2 mt-2 border-t border-gray-200 pt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Identifiants de connexion
              </h4>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe *
              </label>
              <input
                type="password"
                value={addFormData.password}
                onChange={(e) => setAddFormData({...addFormData, password: e.target.value})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Mot de passe"
                required
                autoComplete="new-password"
              />
              <p className="mt-1 text-xs text-gray-500">
                Minimum 8 caractères, avec au moins une majuscule, une minuscule et un chiffre.
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe *
              </label>
              <input
                type="password"
                value={addFormData.confirmPassword}
                onChange={(e) => setAddFormData({...addFormData, confirmPassword: e.target.value})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Répétez le mot de passe"
                required
                autoComplete="new-password"
              />
            </div>

            {/* Section : rôle et permissions */}
            <div className="md:col-span-2 mt-2 border-t border-gray-200 pt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Rôle et permissions
              </h4>
            </div>

            {/* Permission level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Niveau de permission *
              </label>
              <select
                value={addFormData.permissionLevel}
                onChange={(e) => setAddFormData({...addFormData, permissionLevel: e.target.value as 'owner' | 'admin' | 'manager' | 'viewer'})}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="owner">Propriétaire</option>
                <option value="admin">Administrateur</option>
                <option value="manager">Gestionnaire</option>
                <option value="viewer">Observateur</option>
              </select>
            </div>

            {/* Permissions spécifiques */}
            <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">Permissions spécifiques</h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              
              {/* Can manage users checkbox */}
              <label
                htmlFor="canManageUsers"
                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 cursor-pointer hover:border-primary-300"
              >
                <input
                  type="checkbox"
                  id="canManageUsers"
                  checked={addFormData.canManageUsers}
                  onChange={(e) => setAddFormData({...addFormData, canManageUsers: e.target.checked})}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Peut gérer les utilisateurs (créer/modifier/supprimer)</span>
              </label>

              {/* Can manage products checkbox */}
              <label
                htmlFor="canManageProducts"
                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 cursor-pointer hover:border-primary-300"
              >
                <input
                  type="checkbox"
                  id="canManageProducts"
                  checked={addFormData.canManageProducts}
                  onChange={(e) => setAddFormData({...addFormData, canManageProducts: e.target.checked})}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Peut gérer les produits</span>
              </label>

              {/* Can manage offers checkbox */}
              <label
                htmlFor="canManageOffers"
                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 cursor-pointer hover:border-primary-300"
              >
                <input
                  type="checkbox"
                  id="canManageOffers"
                  checked={addFormData.canManageOffers}
                  onChange={(e) => setAddFormData({...addFormData, canManageOffers: e.target.checked})}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Peut gérer les offres</span>
              </label>

              {/* Can view reports checkbox */}
              <label
                htmlFor="canViewReports"
                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700 cursor-pointer hover:border-primary-300"
              >
                <input
                  type="checkbox"
                  id="canViewReports"
                  checked={addFormData.canViewReports}
                  onChange={(e) => setAddFormData({...addFormData, canViewReports: e.target.checked})}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>Peut voir les rapports</span>
              </label>

              </div>

              {/* Active status checkbox */}
              <div className="mt-3 flex items-center border-t border-gray-200 pt-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={addFormData.isActive}
                  onChange={(e) => setAddFormData({...addFormData, isActive: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-gray-700">
                  Actif
                </label>
              </div>
            </div>
            </div>
          </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAddSubmit}
                disabled={submitting || !addFormData.email || !addFormData.firstName || !addFormData.lastName || !addFormData.password || !addFormData.confirmPassword}
                className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Créer l'utilisateur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredMerchantUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun utilisateur trouvé</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedPermission !== 'all' || selectedStatus !== 'all' || (isAdmin && selectedMerchantId !== 'all')
                ? 'Aucun utilisateur ne correspond à vos critères de recherche.'
                : isAdmin
                  ? 'Aucun utilisateur trouvé dans les commerces. Commencez par ajouter des utilisateurs.'
                  : 'Commencez par ajouter des utilisateurs à votre commerce.'}
            </p>
            <button
              onClick={handleAddUser}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              Ajouter un utilisateur
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  {isAdmin && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commerce
                    </th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gestion utilisateurs
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d'ajout
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMerchantUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.userName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.userEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.merchantName}</div>
                        <div className="text-xs text-gray-500">ID: {user.merchantId}</div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.userPhone || 'Non renseigné'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPermissionColor(user.permissionLevel)}`}>
                          {getPermissionIcon(user.permissionLevel)}
                          <span className="ml-1">{getPermissionLabel(user.permissionLevel)}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.canManageUsers ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Oui
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle className="h-4 w-4 mr-1" />
                          Non
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="h-4 w-4 mr-1" />
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        {/* Hide edit button for current user */}
                        {user.userId !== currentUser?.userId && (
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Modifier"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                        )}
                        
                        {/* Hide toggle status button for current user */}
                        {user.userId !== currentUser?.userId && (
                          <button
                            onClick={() => handleToggleStatus(user)}
                            className={user.isActive ? "text-yellow-600 hover:text-yellow-900" : "text-green-600 hover:text-green-900"}
                            title={user.isActive ? "Désactiver" : "Activer"}
                          >
                            {user.isActive ? <UserX className="h-5 w-5" /> : <UserCheck className="h-5 w-5" />}
                          </button>
                        )}
                        
                        {/* Hide delete button for current user */}
                        {user.userId !== currentUser?.userId && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                        
                        {/* Show message for current user */}
                        {user.userId === currentUser?.userId && (
                          <span className="text-sm text-gray-500 italic">(Utilisateur courant)</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Modifier {selectedUser.userName}
              </h3>
              
              <div className="space-y-4">
                {/* Current user info */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Utilisateur:</span> {selectedUser.userName}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Email:</span> {selectedUser.userEmail}
                  </p>
                </div>

                {/* Permission level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau de permission *
                  </label>
                  <select
                    value={editFormData.permissionLevel}
                    onChange={(e) => setEditFormData({...editFormData, permissionLevel: e.target.value as 'owner' | 'admin' | 'manager' | 'viewer'})}
                    className="w-full rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="owner">Propriétaire</option>
                    <option value="admin">Administrateur</option>
                    <option value="manager">Gestionnaire</option>
                    <option value="viewer">Observateur</option>
                  </select>
                </div>

                {/* Permissions spécifiques */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Permissions spécifiques</h4>
                  
                  {/* Can manage users checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editCanManageUsers"
                      checked={editFormData.canManageUsers}
                      onChange={(e) => setEditFormData({...editFormData, canManageUsers: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editCanManageUsers" className="ml-2 block text-sm text-gray-700">
                      Peut gérer les utilisateurs (créer/modifier/supprimer)
                    </label>
                  </div>

                  {/* Can manage products checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editCanManageProducts"
                      checked={editFormData.canManageProducts}
                      onChange={(e) => setEditFormData({...editFormData, canManageProducts: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editCanManageProducts" className="ml-2 block text-sm text-gray-700">
                      Peut gérer les produits
                    </label>
                  </div>

                  {/* Can manage offers checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editCanManageOffers"
                      checked={editFormData.canManageOffers}
                      onChange={(e) => setEditFormData({...editFormData, canManageOffers: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editCanManageOffers" className="ml-2 block text-sm text-gray-700">
                      Peut gérer les offres
                    </label>
                  </div>

                  {/* Can view reports checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="editCanViewReports"
                      checked={editFormData.canViewReports}
                      onChange={(e) => setEditFormData({...editFormData, canViewReports: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editCanViewReports" className="ml-2 block text-sm text-gray-700">
                      Peut voir les rapports
                    </label>
                  </div>

                  {/* Active status checkbox */}
                  <div className="flex items-center pt-2 border-t border-gray-200">
                    <input
                      type="checkbox"
                      id="editIsActive"
                      checked={editFormData.isActive}
                      onChange={(e) => setEditFormData({...editFormData, isActive: e.target.checked})}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="editIsActive" className="ml-2 block text-sm text-gray-700">
                      Actif
                    </label>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MesUtilisateursPage